import React, { useCallback, memo, useState, useContext } from 'react';
import { IView } from '../Types/ViewType';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import UrvalButton, {
  IUrvalButtonProps,
} from '../../../../../../Components/Urval/Components/UrvalButton/UrvalButton';
import { ISortInfo } from '../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import KolumnValjareDialog from '../../../../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialog';
import { useBoolState } from '../../../../../../Hooks/booleanHooks';
import { makeStyles } from 'tss-react/mui';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { IUrvalCondition } from '../../../../../../Types/UrvalTypes';
import { IDetaljurvalInfo } from '../../../../../../Components/Urval/Components/HitIndicator/UrvalHitIndicator';
import ChangedMarker from '../../../../../../Components/ChangedMarker/ChangedMarker';
import { urvalHasValue } from '../../../../../../Components/Urval/Utils/urvalUtils';
import AddNewObjectButton from '../../AddNewObjectButton/AddNewObjectButton';
import LegendMenuButton from '../../../../../../Components/Legend/LegendMenuButton';
import {
  findBusinessColors,
  findBusinessMenuUnusedButtons,
} from '../../../Const/BusinessConfigs';
import { RutinSettingsContext } from '../../RutinAvailabilityContext/RutinAvailabilityContext';
import { AvenyContext } from '../../../../../AvenyContext';
import { useApolloClient } from '@apollo/client';
import { StandardRutinContext } from '../../../../_StandardRutin/StandardRutinContext';
import { ActiveRoutineContext } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { IFunctionMenuItem } from '../../../../../../Utils/ObjektVyUtils';
import {
  findRoutineMenuCustomAddButton,
  findRoutineMenuCustomUrvalButton,
  findRoutineMenuUnusedButtons,
} from '../../../Const/Rutiner';
import { useExportToExcel } from '../../../../../../Components/ExportToExcel/ExportToExcel';
import { NodeUrvalQuery_node_TEDSelection_order } from '../../../../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/_QueryTypes/NodeUrvalQuery';
import { BusinessCollectionMenuList } from './Components/BusinessCollectionMenuList';
import { BusinessCollectionMenuItem } from './Components/BusinessCollectionMenuItem';
import EDDivider from '../../../../../../BaseComponents/Divider/EDDivider';
import { SelectNewDetailType } from '../../../../_StandardRutin/StandardRutin';
import { RapportDialog } from '../../../../Bokning/ObjektVyer/Aktivitet/Components/RapportDialog';
import { ViewDbSettingsContext } from '../../../../Bokning/Rutiner/Kalender/Components/SchedulerComponent/ViewDbSettingsContext';
import { useQuery } from '@apollo/client';
import {
  AvenySYSOptionQuery,
  AvenySYSOptionQueryVariables,
} from '../../../../../../Components/QueryComponents/AvenySYSOptionQuery/_QueryTypes/AvenySYSOptionQuery';
import optionQuery from '../../../../../../Components/QueryComponents/AvenySYSOptionQuery/AvenySYSOptionQuery.gql';
import EDAIChat from '../../../../../../Components/EDAIChat/EDAIChat';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { transformToContextData } from '../../../../../../Components/EDAIChat/Utils/contextDataUtils';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';

export type IProps = IView & {
  /** @ignore */
  refetch: () => void;
  setNewObject?: SelectNewDetailType;
  sortedData: ISortInfo<ICollectionDataType>;
  updateSortOrder: (info: ISortInfo<ICollectionDataType>) => void;
  baseCollection: IClientBusinessModel;
  rutinId?: number;
  baseId: string | null;
  detaljurvalInfo?: IDetaljurvalInfo;
  errorDisable?: boolean;
  customBusinessCollectionMenuItems?: IFunctionMenuItem[];
  isAssociation?: boolean;
  datasetLength: number;
  searchText?: string;
  rows?: ICollectionDataType[];
  setSortInfoFromSelectionOrder?: (
    selectionOrder: NodeUrvalQuery_node_TEDSelection_order[]
  ) => void;
  expanded?: boolean;
};

const useStyles = makeStyles()(
  theme =>
    ({
      button: {
        padding: '8px 0px',
      },
      relativeContainer: {
        position: 'relative',
      },
      changedMarkerContainer: {
        position: 'absolute',
        top: '40px',
        left: '30px',
      },
      paddingLeftMenu: {
        paddingLeft: '10px',
      },
    } as const)
);

const emptyUrval = { pages: [] };

export const CollectionMenuDisabledReason = 'Menyfunktioner avstängda';

/**
Toolbar för att ställa in en Urval, Kolumner.. för en businesscollection
*/
export const BusinessCollectionMenu = (props: IProps) => {
  const { refetch } = props;
  const avenyContext = useContext(AvenyContext);
  const { setOpenChoiceDialog } = avenyContext;
  const { classes, cx } = useStyles();
  const [open, onOpen, onClose] = useBoolState(false);
  const [tabValue, setTabValue] = useState(0);
  const rutinSettingsContext = useContext(RutinSettingsContext);
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const businessColors = findBusinessColors(props.collection.CollectionName);
  const apolloClient = useApolloClient();
  const standardRutinContext = useContext(StandardRutinContext);
  const [showRapportDialog, setShowRapportDialog] = useState(false);
  const [showAIChatDialog, setShowAIChatDialog] = useState(false);
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const modelContext = useContext(ModelContext);
  const calendarSelection = viewDbSettingsContext.calendarSelection;
  const setCalendarSelection = viewDbSettingsContext.setCalendarSelection;

  const handleRefetch = useCallback(() => {
    const urvalCacheInvalidation = standardRutinContext.urvalCacheInvalidation;
    const cache = apolloClient.cache;
    urvalCacheInvalidation?.(cache);

    if (avenyContext.formHasChanged) {
      setOpenChoiceDialog(true, () => {
        refetch();
        apolloClient.refetchQueries({
          include: standardRutinContext.refetchButtonQueries,
        });
      });
    }
    refetch();
    apolloClient.refetchQueries({
      include: standardRutinContext.refetchButtonQueries,
    });
  }, [
    avenyContext.formHasChanged,
    refetch,
    apolloClient,
    standardRutinContext.refetchButtonQueries,
    standardRutinContext.urvalCacheInvalidation,
    setOpenChoiceDialog,
  ]);

  const openValjFalt = useCallback(() => {
    if (avenyContext.formHasChanged) {
      setOpenChoiceDialog(true, () => {
        onOpen();
      });
    } else {
      onOpen();
    }
  }, [avenyContext.formHasChanged, setOpenChoiceDialog, onOpen]);

  const exportToExcel = useExportToExcel({
    collectionName: props.collection.CollectionName,
    columns: props.columns,
    sortProperties: props.sortedData.properties,
    urval: props.urval,
    searchText: props.searchText,
    rows: props.rows,
  });

  const { data: aiIntegrationSysOption } = useQuery<
    AvenySYSOptionQuery,
    AvenySYSOptionQueryVariables
  >(optionQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dbName: activeDatabaseContext?.activeDatabase ?? '',
      subSystemCode: 'SYS',
      optionKey: 'SYS_AI_INTEGRATION_ENABLED',
    },
    skip: !activeDatabaseContext?.activeDatabase,
  });

  let unusedButtons =
    findBusinessMenuUnusedButtons(props.collection.CollectionName) ??
    findRoutineMenuUnusedButtons(activeRoutineContext?.rutinInfo.rutinKod) ??
    [];

  if (!unusedButtons)
    unusedButtons =
      findBusinessMenuUnusedButtons(props.collection.CollectionName) ?? [];

  let CustomAddButton = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    CustomAddButton = findRoutineMenuCustomAddButton(
      activeRoutineContext?.rutinInfo.rutinKod
    );

  function renderUrvalButtonAndChangedMarker() {
    let UseUrvalButton: React.FC<React.PropsWithChildren<IUrvalButtonProps>> =
      findRoutineMenuCustomUrvalButton(
        activeRoutineContext?.rutinInfo.rutinKod
      ) || UrvalButton;

    if (!UseUrvalButton) UseUrvalButton = UrvalButton;
    return (
      <div className={cx(classes.button, classes.relativeContainer)}>
        <UseUrvalButton
          expanded={props.expanded}
          urval={props.urval}
          setUrval={props.setUrval}
          collectionName={props.collection.CollectionName}
          refetch={props.refetch}
          detaljurvalInfo={props.detaljurvalInfo}
          routineID={props.rutinId ?? activeRoutineContext?.rutinInfo.rutinID}
          errorDisable={props.errorDisable}
          errorText={
            rutinSettingsContext.hasRutinStartError
              ? CollectionMenuDisabledReason
              : undefined
          }
          setSortInfoFromSelectionOrder={props.setSortInfoFromSelectionOrder}
        />
        <div className={classes.changedMarkerContainer}>
          {props.detaljurvalInfo != null && urvalHasValue(props.urval) && (
            <ChangedMarker />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <BusinessCollectionMenuList expanded={!!props.expanded}>
        {!unusedButtons.includes('add') && (
          <div className={classes.button}>
            {CustomAddButton != undefined ? (
              <CustomAddButton
                setNewObject={props.setNewObject}
                expanded={props.expanded}
                testId="BusinessCollectionMenu_Add"
              />
            ) : props.setNewObject ? (
              <BusinessCollectionMenuItem
                expanded={props.expanded}
                icon="add"
                onClick={props.setNewObject}
                tooltip={'Skapa ny'}
                disabled={props.errorDisable}
                disabledReason={CollectionMenuDisabledReason}
                testId="BusinessCollectionMenu_Add"
              />
            ) : (
              <AddNewObjectButton
                collection={props.collection}
                onSave={handleRefetch}
                columns={props.columns}
                urval={props.urval || emptyUrval}
                baseCollection={props.baseCollection}
                baseId={props.baseId}
                hasRutinStartError={rutinSettingsContext.hasRutinStartError}
              />
            )}
          </div>
        )}
        {!unusedButtons.includes('urval') &&
          (props.urval || props.setUrval) &&
          activeRoutineContext?.rutinInfo?.rutinID !== 16001 &&
          renderUrvalButtonAndChangedMarker()}
        {!unusedButtons.includes('column') && (
          <div className={classes.button}>
            <BusinessCollectionMenuItem
              expanded={props.expanded}
              icon="view_column"
              onClick={openValjFalt}
              tooltip={'Välj fält, visningstyp och sortering'}
              disabled={props.errorDisable}
              disabledReason={CollectionMenuDisabledReason}
              testId="viewColumnButton"
            />
          </div>
        )}
        {!unusedButtons.includes('refresh') && (
          <div className={classes.button}>
            <BusinessCollectionMenuItem
              expanded={props.expanded}
              icon="refresh"
              onClick={handleRefetch}
              tooltip="Uppdatera"
              disabled={props.errorDisable}
              disabledReason={CollectionMenuDisabledReason}
              testId="BusinessCollectionMenu_Refresh"
            />
          </div>
        )}

        {viewDbSettingsContext.calenderReportSysOption &&
          viewDbSettingsContext.calenderReportSysOption === 'Y' &&
          !unusedButtons.includes('rapport') &&
          activeRoutineContext?.rutinInfo?.rutinID === 16001 && (
            <BusinessCollectionMenuItem
              expanded={props.expanded}
              icon="description"
              onClick={() => {
                setShowRapportDialog(true);
                setCalendarSelection(calendarSelection);
              }}
              tooltip={'Rapport'}
              disabled={
                props.errorDisable ||
                (props?.urval?.pages[0]?.conditionObjects[0].condition as IUrvalCondition[])?.[0]?.values
                  ?.length < 1 ||
                props.datasetLength < 1
              }
              disabledReason={CollectionMenuDisabledReason}
              testId="calender_report_dialog_button"
            />
          )}

        {showRapportDialog && (
          <RapportDialog
            open={showRapportDialog}
            showRapportDialog={setShowRapportDialog}
            urval={
              ((props?.urval?.pages[0]?.conditionObjects[0].condition as IUrvalCondition[])?.[0]?.values ?? []).map(v => ({ id: v.id, value: v.value != null ? String(v.value) : '' }))
            }
            dayCount={viewDbSettingsContext.dayCount}
            currentDate={viewDbSettingsContext.currentDate ?? new Date()}
            currentView={viewDbSettingsContext.currentView}
            sortedData={props.sortedData.rows}
            testId="calender_report_dialog"
          />
        )}

        {!unusedButtons.includes('excel') && (
          <div className={classes.button}>
            <BusinessCollectionMenuItem
              expanded={props.expanded}
              icon="excel_export"
              onClick={() => {
                exportToExcel();
              }}
              tooltip="Exportera till Excel"
              disabled={props.errorDisable || props.datasetLength < 1}
              disabledReason={
                props.errorDisable
                  ? CollectionMenuDisabledReason
                  : props.datasetLength < 1
                  ? 'Inget att exportera'
                  : undefined
              }
              testId="BusinessCollectionMenu_Excel"
            />
          </div>
        )}

        {aiIntegrationSysOption?.db?.SYSOptions_All?.[0]?.OptionData ===
          'Y' && (
          <div className={classes.button}>
            <BusinessCollectionMenuItem
              expanded={props.expanded}
              icon="assistant"
              onClick={() => setShowAIChatDialog(true)}
              tooltip="AI Chat"
              disabled={props.errorDisable || props.datasetLength < 1}
              disabledReason={
                props.errorDisable
                  ? CollectionMenuDisabledReason
                  : props.datasetLength < 1
                  ? 'Inget att chatta om'
                  : undefined
              }
              testId="BusinessCollectionMenu_AIChat"
            />
          </div>
        )}

        {props.customBusinessCollectionMenuItems?.map(customItem => {
          const disabledReason = props.errorDisable
            ? CollectionMenuDisabledReason
            : customItem.disabled
            ? customItem.disabledReason
            : undefined;

          return (
            <div className={classes.button} key={customItem.id}>
              <EDIconButton
                icon={customItem.iconType}
                onClick={customItem.onClick}
                tooltip={customItem.caption}
                margin="none"
                disabled={customItem.disabled || props.errorDisable}
                disabledReason={disabledReason}
                testId={customItem.caption}
              />
            </div>
          );
        })}

        {businessColors.length > 0 && (
          <div className={classes.button}>
            <LegendMenuButton
              posts={businessColors}
              expanded={props.expanded}
            />
          </div>
        )}

        {activeRoutineContext?.rutinInfo?.rutinID === 16001 ? (
          <EDDivider
            marginLeft={props.expanded ? 2 : 0}
            marginRight={props.expanded ? 3 : 30}
          />
        ) : undefined}

        {!unusedButtons.includes('urval') &&
          (props.urval || props.setUrval) &&
          activeRoutineContext?.rutinInfo?.rutinID === 16001 &&
          renderUrvalButtonAndChangedMarker()}
      </BusinessCollectionMenuList>
      {props.setColumns && (
        <KolumnValjareDialog
          valdaKolumner={props.columns}
          collectionName={props.collection.CollectionName}
          setValdaKolumner={props.setColumns}
          sortedData={props.sortedData}
          updateSortOrder={props.updateSortOrder}
          tabValue={tabValue}
          setTabValue={setTabValue}
          open={open}
          onClose={onClose}
          inAssociation={
            props.baseCollection.CollectionName !==
            props.collection.CollectionName
          }
        />
      )}

      {aiIntegrationSysOption?.db?.SYSOptions_All?.[0]?.OptionData === 'Y' && (
        <EDDialog
          open={showAIChatDialog}
          onClose={() => setShowAIChatDialog(false)}
          title="AI Chat"
          maxWidth="md"
          fullWidth
        >
          <EDAIChat
            height={500}
            contextData={transformToContextData(
              props.sortedData.rows,
              props.columns,
              props.collection,
              modelContext.avenyCollectionMap,
              unusedButtons.includes('column')
            )}
            testId="AI-Chat-Dialog"
          />
        </EDDialog>
      )}
    </>
  );
};

export default memo(BusinessCollectionMenu);
